import type { SupabaseClient } from '@supabase/supabase-js';
import { KF_ICON, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';
import { sendVarioMessage } from '../vario-iframe.js';
import { ensureKfDialogShell, kfEscapeHtml, showKfDialogResult, KF_DLG_ICON } from './dialog-shell.js';

interface UninstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { tenantId: string };
}

interface UninstallResult {
  ok: boolean;
  removed_credential_secret: boolean;
  removed_offline_token_secret: boolean;
  removed_storefronts_count: number;
}

/**
 * Vario opens this slot after the user clicked "Deinstallieren". The flow mirrors the
 * @vario-software/vario-app-framework-frontend `uninstallFromErp` helper:
 *   1. Tear down our server-side state (kaufland_uninstall RPC).
 *   2. Send `{ requestUninstall: true }` to the Vario host via postMessage.
 *   3. Wait for the Vario host to reply with `{ uninstallState: true|false }`.
 *      `true` ⇒ Vario removed the app from the tenant.
 *      `false` ⇒ Vario refused, the app stays installed.
 *
 * Sending `installationComplete` (which we tried earlier) is the install flow's success message
 * and does NOT trigger Vario's uninstall path — that was the reason the app was not actually
 * being removed.
 *
 * Darstellung: derselbe kf-page-Dialog-Rahmen wie `/#/install` (App-Header + schwebende Karte,
 * Vario-grüner Akzent), Höhe per Auto-Grow (`ensureKfDialogShell` + `initVarioIframeHeight('auto')`
 * in main.ts) — kein Vollflächen-Slot, daher kein `useFullHeight`.
 */
export async function renderUninstall(ctx: UninstallContext) {
  ensureKfDialogShell();
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page kf-dialog">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle">
            <div class="heyebrow">Kaufland · Deinstallation</div>
            <h1>Kaufland-App deinstallieren</h1>
          </div>
          <div class="hactions">
            <span class="hstatus pending" id="kl-uninst-pill"><span class="d"></span>Deinstallation läuft …</span>
          </div>
        </header>

        <div class="card" id="kl-uninst-card">
          <p class="dlg-intro">Die Verbindung zu <b>Kaufland</b> wird deaktiviert und die gespeicherten
            Zugangsdaten werden vernichtet. Deine <b>Konfiguration bleibt erhalten</b> — Marktplatz-Zuordnungen
            und Kanal-Verknüpfungen sind nach einer erneuten Installation sofort wieder da.</p>

          <div class="sec-lab">Fortschritt</div>

          <div class="dlg-steps">
            <div class="dlg-step running" data-step="server">
              <span class="dlg-step-ic"><span class="dlg-spin"></span></span>
              <span class="dlg-step-txt">Verbindung deaktivieren, Zugangsdaten vernichten</span>
            </div>
            <div class="dlg-step" data-step="vario">
              <span class="dlg-step-ic"></span>
              <span class="dlg-step-txt">App in Vario entfernen</span>
            </div>
          </div>

          <div class="dlg-msg" id="uninstall-status" aria-live="polite"></div>
        </div>
      </main>
    </div>
  `;

  const card = ctx.contentEl.querySelector<HTMLDivElement>('#kl-uninst-card')!;
  const pill = ctx.contentEl.querySelector<HTMLSpanElement>('#kl-uninst-pill')!;
  const statusEl = ctx.contentEl.querySelector<HTMLDivElement>('#uninstall-status')!;

  function setStatus(text: string, kind: 'info' | 'ok' | 'error' = 'info') {
    if (!text) {
      statusEl.className = 'dlg-msg';
      statusEl.innerHTML = '';
      return;
    }
    const icon = kind === 'ok' ? KF_DLG_ICON.check : kind === 'error' ? KF_DLG_ICON.alert : KF_DLG_ICON.info;
    statusEl.className = `dlg-msg show ${kind}`;
    statusEl.innerHTML = `${icon}<span>${kfEscapeHtml(text)}</span>`;
  }

  function setPill(text: string, kind: 'pending' | 'ok' | 'error') {
    pill.className = kind === 'ok' ? 'hstatus' : `hstatus ${kind}`;
    pill.innerHTML = `<span class="d"></span>${kfEscapeHtml(text)}`;
  }

  function setStep(step: 'server' | 'vario', state: 'running' | 'done' | 'failed') {
    const el = ctx.contentEl.querySelector<HTMLDivElement>(`.dlg-step[data-step="${step}"]`);
    if (!el) return;
    el.className = `dlg-step ${state}`;
    el.querySelector<HTMLElement>('.dlg-step-ic')!.innerHTML =
      state === 'running' ? '<span class="dlg-spin"></span>'
      : state === 'done' ? KF_DLG_ICON.check
      : KF_ICON.close;
  }

  // ---- 1. Server-side teardown -------------------------------------------
  let serverOk = true;
  try {
    const { data, error } = await ctx.supabase.rpc('kaufland_uninstall');
    if (error) {
      serverOk = false;
      setStep('server', 'failed');
      setStatus(`Server-Cleanup fehlgeschlagen: ${error.message}`, 'error');
    } else {
      const r = (Array.isArray(data) ? data[0] : data) as UninstallResult | undefined;
      setStep('server', 'done');
      if (r?.ok) {
        const n = r.removed_storefronts_count ?? 0;
        setStatus(
          n > 0
            ? `Marktplatz-Verbindung deaktiviert (Konfiguration inkl. ${n} Storefront-Verknüpfungen bleibt erhalten).`
            : 'Marktplatz-Verbindung deaktiviert.',
          'info',
        );
      } else {
        setStatus('Keine Verbindung gespeichert — Vario-Status wird zurückgesetzt.', 'info');
      }
    }
  } catch (e) {
    serverOk = false;
    setStep('server', 'failed');
    setStatus(`Server-Cleanup-Ausnahme: ${(e as Error).message}`, 'error');
  }

  // ---- 2. Tell Vario to remove the app, wait for confirmation ------------
  setStep('vario', 'running');
  const ok = await requestVarioUninstall();

  if (ok) {
    setPill('Deinstalliert', 'ok');
    showKfDialogResult(card, {
      kind: 'neutral',
      icon: KF_DLG_ICON.trash,
      title: 'Kaufland-App wurde deinstalliert',
      text: serverOk
        ? 'Die Verbindung ist deaktiviert und die Zugangsdaten sind vernichtet. Deine Konfiguration bleibt gespeichert und wird bei einer erneuten Installation wieder genutzt.'
        : 'Die App ist in Vario entfernt, unser Server-Cleanup hat dabei aber einen Fehler gemeldet. Bitte informiere den Support, bevor du die App neu installierst.',
      note: serverOk ? 'Konfiguration bleibt erhalten' : undefined,
    });
  } else {
    setPill('Abgelehnt', 'error');
    showKfDialogResult(card, {
      kind: 'error',
      icon: KF_ICON.ban,
      title: 'Vario hat die Deinstallation abgelehnt',
      text: 'Die App ist weiterhin installiert. Bitte versuche es später erneut oder kontaktiere den Support.',
    });
  }
}

function requestVarioUninstall(): Promise<boolean> {
  return new Promise((resolve) => {
    const listener = (e: MessageEvent) => {
      const data = e.data as { uninstallState?: boolean } | null;
      if (!data || typeof data !== 'object' || typeof data.uninstallState !== 'boolean') return;
      window.removeEventListener('message', listener);
      resolve(data.uninstallState);
    };
    window.addEventListener('message', listener);

    // 30s safety timeout — if Vario never answers, fail closed.
    setTimeout(() => {
      window.removeEventListener('message', listener);
      resolve(false);
    }, 30_000);

    sendVarioMessage({ requestUninstall: true });
  });
}
